import { CatalogEntryButtonFilterBy } from '@/components/content/CatalogEntryList/parts/ButtonFilterBy';
import { CategoryTabs } from '@/components/content/CatalogEntryList/parts/CategoryTabs';
import { CatalogEntryListCompareCollector } from '@/components/content/CatalogEntryList/parts/Compare/Collector';
import { CatalogEntryListFacetChips } from '@/components/content/CatalogEntryList/parts/FacetChips';
import { CatalogEntryListPagination } from '@/components/content/CatalogEntryList/parts/Pagination';
import { CatalogEntryListProductGrid } from '@/components/content/CatalogEntryList/parts/ProductGrid';
import { CatalogEntryListSort } from '@/components/content/CatalogEntryList/parts/Sort';
import { catalogEntryListContainerSX } from '@/components/content/CatalogEntryList/styles/container';
import { useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import { useCompareCollector } from '@/data/Content/CompareCollector';
import { ContentProvider } from '@/data/context/content';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import { ID } from '@/data/types/Basic';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { FC, Fragment, useEffect, useMemo } from 'react';

// eslint-disable-next-line complexity
export const CatalogEntryList: FC<{ id: ID; isFilter?: boolean; isFilterChips?: boolean }> = ({
	id,
	isFilter,
	isFilterChips,
}) => {
	const catalogEntryList = useCatalogEntryList(id);
	const { entitled, loading, filteredParams, onTriggerMarketingEvent } = catalogEntryList;
	const { searchTerm } = filteredParams;
	const compare = useCompareCollector(id);
	const { compareEnabled, compareState } = compare;
	const contextValue = useMemo(
		() => ({ ...catalogEntryList, ...compare }),
		[catalogEntryList, compare]
	);

	const isMobile = useIsMobile();

	useEffect(() => {
		if (searchTerm) {
			onTriggerMarketingEvent(searchTerm);
		}
	}, [searchTerm, onTriggerMarketingEvent]);

	if (!loading && !entitled && !searchTerm) {
		return null;
	}
	if (isFilterChips) {
		return (
			<ContentProvider value={contextValue}>
				<CatalogEntryListFacetChips id={id} isFilter={true} />
			</ContentProvider>
		);
	}
	if (isFilter) {
		return (
			<ContentProvider value={contextValue}>
				<Typography
					variant={'h4'}
					sx={{ fontSize: '15px', marginBottom: '9px', fontWeight: 'bold' }}
				>
					{'Ordenar por'}
				</Typography>
				<CatalogEntryListSort />
			</ContentProvider>
		);
	}
	return (
		<ContentProvider value={contextValue}>
			<Stack
				sx={catalogEntryListContainerSX}
				id="catalog-entry-list-product-container"
				data-testid="catalog-entry-list-product-container"
			>
				{isMobile ? (
					<Fragment>
						{searchTerm ? (
							<Typography
								component={'p'}
								sx={{
									fontSize: '18px',
									marginBottom: '15px',
									mt: '20px',
									fontFamily: 'Open Sans, "Helvetica Neue", Arial, sans-serif',
									color: '#2c2c2c',
								}}
							>
								{`Resultados de búsqueda para `}
								<Typography
									component={'span'}
									sx={{
										color: '#2c2c2c',
										fontWeight: '800',
										textTransform: 'uppercase',
										fontSize: '17px',
										mx: '3px',
									}}
								>
									{` "${searchTerm}" `}
								</Typography>
							</Typography>
						) : null}
					</Fragment>
				) : null}
				{isMobile ? <CatalogEntryListFacetChips id={id} /> : null}
				{isMobile ? <CategoryTabs /> : null}
				{!isMobile ? (
					<Fragment>
						{searchTerm ? (
							<Typography
								component={'p'}
								sx={{
									fontSize: '18px',
									marginBottom: '15px',
									fontFamily: 'Open Sans, "Helvetica Neue", Arial, sans-serif',
									color: '#2c2c2c',
									mt: '55px',
									display: 'flex',
									alignItems: 'center',
								}}
							>
								{`Resultados de búsqueda para `}
								<Typography
									component={'span'}
									sx={{
										color: '#2c2c2c',
										fontWeight: '800',
										textTransform: 'uppercase',
										mx: '3px',
										fontSize: '18px',
									}}
								>
									{` "${searchTerm}" `}
								</Typography>
								{` Encontramos (`}
								{loading ? (
									<Skeleton sx={{ height: '27px', width: '55px', mr: '3px' }} />
								) : (
									<Typography
										sx={{ mr: '3px', fontSize: '18px' }}
										component={'span'}
									>{`${contextValue.total?.toLocaleString('es-MX')}`}</Typography>
								)}
								{` coincidencias.)`}
							</Typography>
						) : null}
					</Fragment>
				) : null}
				<Box
					sx={{
						borderTop: '1px solid #E1E1E1',
						pt: '16px',
						mb: '19px',
						display: { xs: 'block', md: 'flex' },
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					{!isMobile ? (
						<Box sx={{ overflow: { xs: 'scroll', md: 'visible' } }}>
							<CatalogEntryListFacetChips id={id} />
						</Box>
					) : null}
					<Box>{!isMobile ? <CatalogEntryListSort /> : <CatalogEntryButtonFilterBy />}</Box>

					{isMobile ? (
						<Box sx={{ overflow: { xs: 'scroll', md: 'visible' } }}>
							<CatalogEntryListFacetChips id={id} isFilter={'true'} />
						</Box>
					) : null}
				</Box>

				<CatalogEntryListProductGrid />

				<CatalogEntryListPagination />
			</Stack>
			{compareEnabled && compareState.len > 0 ? <CatalogEntryListCompareCollector /> : null}
		</ContentProvider>
	);
};
